"use client";

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useModalScrollLock } from "./use-modal-scroll-lock";

/**
 * LOT 60 — ModalShell
 *
 * Wrapper standard pour toutes les modales custom de l'app. Garantit :
 *  - Render dans un portal sur `document.body` → immune aux ancêtres
 *    qui auraient un `transform`/`filter`/`will-change` cassant le
 *    `position: fixed`.
 *  - Scroll lock sur `body` quand la modale est ouverte → l'écran ne
 *    "flotte" plus pendant qu'on a la modale ouverte.
 *  - Centrage VIEWPORT (pas document) via `fixed inset-0` + flex.
 *  - ESC pour fermer.
 *  - Click sur backdrop pour fermer (optionnel).
 *  - Drawer mobile (slide-up depuis le bas) automatique sur < sm.
 *  - Le header est sticky pour qu'on garde le titre + close en vue
 *    même quand on scrolle dans le contenu de la modale.
 *
 * Usage :
 *   <ModalShell open={!!editing} onClose={() => setEditing(null)} title="...">
 *     ... contenu ...
 *   </ModalShell>
 *
 * Pour des modales sans header standard (qui veulent leur propre layout
 * complet), passe `bare` : on ne rend que le portal + backdrop + panel
 * sans le header sticky, et le contenu prend tout.
 */
/** Les modales ouvertes, de la plus ancienne à celle du dessus. */
const PILE: symbol[] = [];

export function ModalShell({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = "min(480px, 100%)",
  bare = false,
  closeOnBackdrop = true,
  zIndex = 100,
  cote,
  sansFond = false,
  entete,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Optional sub-line under the title (eg. "Mars 2026"). */
  subtitle?: string;
  children: React.ReactNode;
  /** Optional fixed footer (actions row), kept outside the scrollable body. */
  footer?: React.ReactNode;
  /** CSS width-string (default 480px responsive). */
  width?: string;
  /** Skip standard header — use raw children only. */
  bare?: boolean;
  /** Click outside panel closes (default true). */
  closeOnBackdrop?: boolean;
  /** z-index of the overlay (default 100). */
  zIndex?: number;
  /**
   * Deux fiches côte à côte : chacune se décale de sa moitié vers son côté,
   * sur un écran assez large pour les tenir de front. En deçà, elles
   * s'empilent comme deux modales ordinaires.
   */
  cote?: "gauche" | "droite";
  /**
   * Sans fond : la modale laisse passer les clics autour d'elle, vers celle
   * d'en dessous — la seconde fiche d'un binôme ne doit pas rendre la
   * première inaccessible.
   */
  sansFond?: boolean;
  /**
   * En-tête sur mesure, à la place du titre et du sous-titre (la croix de
   * fermeture reste). Il ne défile pas avec le contenu.
   */
  entete?: React.ReactNode;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  /** Le geste en cours a-t-il commencé hors du panneau ? */
  const departHorsPanneau = useRef(false);

  /**
   * L'événement vient-il bien de CETTE modale ?
   *
   * React fait remonter les événements d'un portail le long de l'arbre des
   * composants, et non du DOM. Une modale imbriquée — la question posée
   * par-dessus une fiche, un menu, une visionneuse — envoie donc ses clics
   * jusqu'ici, où sa cible, située dans un autre portail, passait pour
   * « hors du panneau » : la fiche se fermait dès qu'on répondait à la
   * question posée au-dessus d'elle.
   */
  const depuisCetteModale = (e: React.PointerEvent) =>
    !!overlayRef.current?.contains(e.target as Node);

  // LOT 244 — Verrou de scroll body+html via hook partagé (gère les
  // modales empilées via compteur global).
  useModalScrollLock(open);

  /*
   * Échap ne ferme que la modale du DESSUS.
   *
   * Chacune écoutait le clavier pour son compte : avec deux fiches ouvertes
   * côte à côte, ou une question posée par-dessus une fiche, un seul appui
   * fermait tout — et la saisie des deux partait avec. La pile retient
   * l'ordre d'ouverture ; `onClose` passe par une ref, sans quoi chaque
   * rendu du parent réinscrirait la modale en haut de la pile.
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });
  useEffect(() => {
    if (!open) return;
    const moi = Symbol("modale");
    PILE.push(moi);
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && PILE[PILE.length - 1] === moi) {
        // Marqué comme traité : un écouteur de page (l'arbre famille) ne
        // doit pas, en plus, annuler sa propre sélection.
        e.preventDefault();
        onCloseRef.current();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const i = PILE.indexOf(moi);
      if (i >= 0) PILE.splice(i, 1);
    };
  }, [open]);

  // Don't render anything until window is available (SSR-safe)
  if (typeof window === "undefined") return null;
  if (!open) return null;

  const overlay = (
    /*
     * Fermeture au clic extérieur : on regarde où le geste COMMENCE, pas
     * seulement où il finit.
     *
     * En sélectionnant du texte dans un champ, on relâche souvent le bouton
     * hors de la fiche. Le navigateur émet alors un `click` sur l'ancêtre
     * commun — le fond — et la fiche se fermait en pleine saisie, tout le
     * travail perdu. On ne ferme donc que si le pointeur a été enfoncé ET
     * relâché en dehors du panneau.
     */
    <div
      ref={overlayRef}
      className={`fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4 ${sansFond ? "pointer-events-none" : ""}`}
      style={{ zIndex }}
      onPointerDown={(e) => {
        if (!depuisCetteModale(e)) { departHorsPanneau.current = false; return; }
        departHorsPanneau.current = !panelRef.current?.contains(e.target as Node);
      }}
      onPointerUp={(e) => {
        if (!depuisCetteModale(e)) return;
        const finHorsPanneau = !panelRef.current?.contains(e.target as Node);
        if (closeOnBackdrop && departHorsPanneau.current && finHorsPanneau) onClose();
        departHorsPanneau.current = false;
      }}
    >
      {/* Backdrop */}
      {!sansFond && <div className="absolute inset-0 modal-backdrop" aria-hidden />}

      {/* Panel — drawer-style on mobile, centered on desktop */}
      <div
        ref={panelRef}
        className={`relative modal-panel rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col w-full sm:w-auto max-h-[90dvh] sm:max-h-[85vh] modal-enter pointer-events-auto transition-[translate] duration-300 ${
          cote === "gauche" ? "lg:-translate-x-[calc(50%+8px)]" : cote === "droite" ? "lg:translate-x-[calc(50%+8px)]" : ""
        }`}
        style={{ width }}
        role="dialog"
        aria-modal="true"
      >
        {!bare && (title || entete) && (
          <div className="px-5 pt-5 pb-3 border-b border-border-default/40 shrink-0 flex items-start justify-between">
            {entete ? <div className="min-w-0 flex-1">{entete}</div> : (
              <div className="min-w-0">
                <h2 className="text-base font-bold truncate">{title}</h2>
                {subtitle && <p className="text-[11px] text-text-muted mt-0.5 truncate">{subtitle}</p>}
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-surface-secondary hover:bg-sand-dark/50 dark:hover:bg-white/10 transition-colors text-text-muted hover:text-foreground shrink-0 ml-3 pressable"
              aria-label="Fermer"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Scrollable body.
            LOT 244 — `overscroll-contain` empêche le scroll chaining
            vers la page en arrière-plan quand on atteint le bord. */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {children}
        </div>

        {/* Optional sticky footer */}
        {footer && (
          <div className="px-5 py-3 border-t border-border-default/40 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
